/**
 * Draft Storage
 *
 * Low-level persistence layer for form autosave drafts. Wraps localStorage /
 * sessionStorage with the guarantees the autosave hook relies on:
 *
 * - Never throws. Storage can be unavailable (Safari private mode), full, or
 *   hold corrupt JSON written by an older build. Every failure degrades to
 *   "no draft" instead of breaking the page the form lives on.
 * - Keys are scoped per user + tenant. This app is multi-tenant and one user
 *   can belong to several workspaces, so an unscoped key would leak tenant A's
 *   draft into tenant B's form.
 * - Entries carry a schema version and a timestamp, so drafts written before a
 *   form's shape changed are discarded rather than restored into fields that
 *   no longer exist.
 */

const KEY_PREFIX = 'aos:draft';
const KEY_VERSION = 'v1';

/** Drafts older than this are treated as expired and ignored. */
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export type DraftStorageType = 'local' | 'session';

export interface DraftEnvelope<T = unknown> {
  /** Form-shape version. A mismatch discards the draft. */
  version: number;
  /** Epoch ms the draft was written. Drives TTL expiry and the "saved at" label. */
  savedAt: number;
  /** True when the draft holds edits that never reached the server (offline). */
  pendingSync: boolean;
  values: T;
}

export interface DraftKeyParts {
  formKey: string;
  userId?: number | string | null;
  tenantId?: number | string | null;
  /** Distinguishes drafts of the same form bound to different records. */
  entityId?: number | string | null;
}

/**
 * Coerces an id segment to a safe string. Anything that is not a string or
 * finite number (an object slipping through from a store, NaN) would stringify
 * to '[object Object]' or 'NaN' and silently collapse distinct users' drafts
 * onto one key, so it falls back to the caller's placeholder instead.
 */
function idSegment(value: number | string | null | undefined, fallback: string): string {
  if (typeof value === 'string') return value.trim() === '' ? fallback : value;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : fallback;
  return fallback;
}

/**
 * Builds the storage key. Missing user/tenant collapse to 'anon'/'global' so a
 * key is always well-formed — callers gate on `enabled` rather than on identity
 * being resolved, and a draft written before the tenant loaded would otherwise
 * land under a key nothing ever reads back.
 */
export function buildDraftKey({ formKey, userId, tenantId, entityId }: DraftKeyParts): string {
  const user = idSegment(userId, 'anon');
  const tenant = idSegment(tenantId, 'global');
  const base = `${KEY_PREFIX}:${KEY_VERSION}:${user}:${tenant}:${formKey}`;
  const entity = idSegment(entityId, '');
  return entity === '' ? base : `${base}:${entity}`;
}

function getStore(type: DraftStorageType): Storage | null {
  try {
    const store = type === 'session' ? window.sessionStorage : window.localStorage;
    // Touch the API — merely reading the property succeeds in private mode
    // while any write throws, so probe with a real round-trip.
    const probe = `${KEY_PREFIX}:__probe__`;
    store.setItem(probe, '1');
    store.removeItem(probe);
    return store;
  } catch {
    return null;
  }
}

/** Removes every draft entry across both storages. Used on logout. */
export function clearAllDrafts(): void {
  (['local', 'session'] as DraftStorageType[]).forEach(type => {
    const store = getStore(type);
    if (!store) return;
    try {
      const doomed: string[] = [];
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i);
        if (key?.startsWith(`${KEY_PREFIX}:`)) doomed.push(key);
      }
      doomed.forEach(key => store.removeItem(key));
    } catch {
      // Storage disappeared mid-iteration; nothing useful to do.
    }
  });
}

/**
 * Drops the oldest drafts to make room after a quota error. Returns true when
 * at least one entry was evicted, i.e. when a retry is worth attempting.
 */
function evictOldestDraft(store: Storage): boolean {
  try {
    let oldestKey: string | null = null;
    let oldestAt = Number.POSITIVE_INFINITY;

    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (!key?.startsWith(`${KEY_PREFIX}:`)) continue;
      const raw = store.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw) as DraftEnvelope;
        const savedAt = typeof parsed?.savedAt === 'number' ? parsed.savedAt : 0;
        if (savedAt < oldestAt) {
          oldestAt = savedAt;
          oldestKey = key;
        }
      } catch {
        // Corrupt entry — evicting it is strictly an improvement.
        oldestKey = key;
        oldestAt = -1;
      }
    }

    if (oldestKey) {
      store.removeItem(oldestKey);
      return true;
    }
  } catch {
    // fall through
  }
  return false;
}

export function writeDraft<T>(
  key: string,
  values: T,
  options: { version: number; storage: DraftStorageType; pendingSync?: boolean },
): DraftEnvelope<T> | null {
  const store = getStore(options.storage);
  if (!store) return null;

  const envelope: DraftEnvelope<T> = {
    version: options.version,
    savedAt: Date.now(),
    pendingSync: options.pendingSync ?? false,
    values,
  };

  let serialized: string;
  try {
    serialized = JSON.stringify(envelope);
  } catch {
    // Value graph is not serializable (cycles, File objects, ...). Autosaving
    // a partial snapshot would be worse than not autosaving at all.
    return null;
  }

  try {
    store.setItem(key, serialized);
    return envelope;
  } catch {
    // Most likely QuotaExceededError. Evict the oldest draft and retry once;
    // if it still fails, give up quietly rather than surfacing an error the
    // user can do nothing about.
    if (evictOldestDraft(store)) {
      try {
        store.setItem(key, serialized);
        return envelope;
      } catch {
        return null;
      }
    }
    return null;
  }
}

export function readDraft<T>(
  key: string,
  options: { version: number; storage: DraftStorageType; ttlMs?: number },
): DraftEnvelope<T> | null {
  const store = getStore(options.storage);
  if (!store) return null;

  let raw: string | null;
  try {
    raw = store.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: DraftEnvelope<T>;
  try {
    parsed = JSON.parse(raw) as DraftEnvelope<T>;
  } catch {
    removeDraft(key, options.storage);
    return null;
  }

  if (!parsed || typeof parsed !== 'object' || !('values' in parsed)) {
    removeDraft(key, options.storage);
    return null;
  }

  // Shape changed since this draft was written — restoring it could populate
  // fields that no longer exist or miss required ones.
  if (parsed.version !== options.version) {
    removeDraft(key, options.storage);
    return null;
  }

  const ttl = options.ttlMs ?? DRAFT_TTL_MS;
  if (typeof parsed.savedAt !== 'number' || Date.now() - parsed.savedAt > ttl) {
    removeDraft(key, options.storage);
    return null;
  }

  return parsed;
}

export function removeDraft(key: string, storage: DraftStorageType): void {
  const store = getStore(storage);
  if (!store) return;
  try {
    store.removeItem(key);
  } catch {
    // Nothing actionable.
  }
}
